import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users, Database, HardDrive, FileStack, Activity, ArrowRight, CheckCircle2, XCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { fmtBytes, fmtDate } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { DASHBOARD } from '@/constants/testIds';
import { Skeleton } from '@/components/ui/skeleton';

function Metric({ label, value, sub, icon: Icon, testId, accent }) {
    return (
        <div
            data-testid={testId}
            className="rounded-sm border border-border bg-card p-5 relative overflow-hidden"
        >
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">{label}</div>
                <Icon size={16} className={accent ? 'text-primary' : 'text-muted-foreground'} />
            </div>
            <div className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight">
                {value}
            </div>
            {sub && (
                <div className="mt-2 text-xs text-muted-foreground">{sub}</div>
            )}
        </div>
    );
}

export default function AdminDashboard() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get('/dashboard/admin');
                setData(data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading || !data) {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-28 rounded-sm" />
                ))}
            </div>
        );
    }

    return (
        <div data-testid={DASHBOARD.root} className="space-y-8">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-1">
                        Welcome back, {user?.name || 'Admin'}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                        Manage users, spaces, and browse read-only DigitalOcean Spaces content across your
                        entire organisation.
                    </p>
                </div>
                <div className="grid-bg hidden md:block w-52 h-16 rounded-sm border border-border" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Metric
                    label="Users"
                    value={data.counts.users}
                    sub={`${data.counts.active_users} active`}
                    icon={Users}
                    testId={DASHBOARD.metricUsers}
                />
                <Metric
                    label="Spaces"
                    value={data.counts.spaces}
                    sub={`${data.counts.active_spaces} active`}
                    icon={Database}
                    testId={DASHBOARD.metricSpaces}
                    accent
                />
                <Metric
                    label="Storage Used"
                    value={fmtBytes(data.storage.total_size)}
                    sub={`${data.storage.total_folders} folders`}
                    icon={HardDrive}
                    testId={DASHBOARD.metricStorage}
                />
                <Metric
                    label="Total Files"
                    value={data.storage.total_files.toLocaleString()}
                    sub={`${data.counts.recent_logins} logins · 7d`}
                    icon={FileStack}
                    testId={DASHBOARD.metricFiles}
                />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-sm border border-border bg-card">
                    <div className="p-5 border-b border-border flex items-center justify-between">
                        <div className="font-display text-lg font-bold">Spaces</div>
                        <button
                            onClick={() => nav('/spaces')}
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                            Manage <ArrowRight size={12} />
                        </button>
                    </div>
                    <div className="divide-y divide-border">
                        {data.spaces.length === 0 ? (
                            <div className="p-8 text-center text-sm text-muted-foreground">
                                No Spaces configured yet.{' '}
                                <button
                                    onClick={() => nav('/spaces')}
                                    className="text-primary hover:underline"
                                >
                                    Add your first Space →
                                </button>
                            </div>
                        ) : (
                            data.spaces.map((s) => (
                                <button
                                    key={s.id}
                                    onClick={() => nav(`/explorer/${s.id}`)}
                                    className="w-full text-left px-5 py-3 flex items-center justify-between row-hover hover:bg-muted/50"
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium truncate">{s.name}</span>
                                            <span className="text-xs text-muted-foreground">
                                                {s.region}
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {fmtBytes(s.size)} · {s.files.toLocaleString()} files ·{' '}
                                            {s.folders} folders
                                        </div>
                                    </div>
                                    <div>
                                        {s.connected === true ? (
                                            <CheckCircle2 size={16} className="text-emerald-500" />
                                        ) : s.connected === false ? (
                                            <XCircle size={16} className="text-destructive" />
                                        ) : (
                                            <span className="text-[10px] text-muted-foreground">
                                                unscanned
                                            </span>
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                <div className="rounded-sm border border-border bg-card">
                    <div className="p-5 border-b border-border">
                        <div className="font-display text-lg font-bold flex items-center gap-2">
                            <Activity size={16} /> Activity
                        </div>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        {data.recent_activity.length === 0 ? (
                            <div className="p-6 text-sm text-muted-foreground">No activity yet.</div>
                        ) : (
                            data.recent_activity.map((a, i) => (
                                <div
                                    key={i}
                                    className="px-5 py-3 border-b border-border last:border-0"
                                >
                                    <div className="text-xs font-medium text-primary">
                                        {a.action.replace(/_/g, ' ')}
                                    </div>
                                    <div className="text-sm text-foreground/90 mt-0.5">
                                        {a.description}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-1">
                                        {fmtDate(a.created_at)}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
